import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';
import * as FaIcons from 'react-icons/fa';
import * as IoIcons from 'react-icons/io5';
import RegistrationForm from '../components/RegistrationForm';

const { FiCheck, FiArrowRight, FiClock, FiShield, FiTrendingUp, FiTarget, FiUsers, FiAward, FiLock } = FiIcons;
const { BiWorld, BiBrain } = BiIcons;
const { FaGem, FaWhatsapp } = FaIcons;
const { IoDiamondOutline } = IoIcons;

const LandingPage = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 24, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const scrollToForm = () => {
    document.getElementById('registration-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans selection:bg-amber-500/30 overflow-x-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-[#020617]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FaGem} className="text-2xl text-amber-400" />
            <span className="font-bold text-xl tracking-wide text-white">DMC <span className="text-amber-400">Diamond 2.0</span></span>
          </div>
          <button 
            onClick={scrollToForm}
            className="hidden md:flex bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-6 py-2 rounded-full hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all duration-300 transform hover:scale-105"
          >
            Reserve Seat
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-4 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-amber-500/20 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-amber-400 text-sm font-semibold tracking-wide uppercase">Exclusive Valentine Special – Private Invite Only</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Diamond 2.0 <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600">
              The Complete Wealth, Health & Authority Ecosystem
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Build ₹1L–₹10L/month income, become a certified expert, launch your digital business, and build your legacy brand — all inside one ecosystem.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button 
              onClick={scrollToForm}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg rounded-full hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] transition-all duration-300 flex items-center justify-center gap-2"
            >
              👉 Reserve My Private Seat
            </button>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm text-slate-400"
          >
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiClock} className="text-amber-400" />
              <span>Live Session: Tomorrow 8PM</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiAward} className="text-amber-400" />
              <span>₹40,000 Live Discount</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiLock} className="text-amber-400" />
              <span>Closed Room Session</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-900/50 relative">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-12">Is This You Right Now?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Struggling to scale income beyond basics",
              "No consistent leads or high-ticket offers",
              "Confused about automation & funnels"
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-red-500/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-500 text-xl font-bold">?</span>
                </div>
                <p className="text-slate-300">{item}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 p-6 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl border border-amber-500/20">
            <p className="text-xl md:text-2xl font-semibold text-amber-100">
              "You don’t need another course… <span className="text-amber-400">you need a SYSTEM.</span>"
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-400 font-semibold tracking-wider uppercase text-sm">Introducing</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Diamond 2.0 Ecosystem</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">The All-in-One Life + Business Transformation System</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="col-span-1 lg:col-span-3 grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { icon: FiUsers, label: "Certified Healer" },
                { icon: BiBrain, label: "Digital Coach" },
                { icon: FiTarget, label: "Funnel Builder" },
                { icon: FiTrendingUp, label: "High Ticket Closer" },
                { icon: IoDiamondOutline, label: "Passive Income" },
              ].map((role, i) => (
                <div key={i} className="flex flex-col items-center p-4 bg-white/5 rounded-xl border border-white/5 hover:border-amber-500/30 transition-all">
                  <SafeIcon icon={role.icon} className="text-3xl text-amber-400 mb-3" />
                  <span className="text-sm font-medium text-center">{role.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HEAL Ecosystem */}
      <section className="py-20 bg-gradient-to-b from-[#020617] to-emerald-950/20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <SafeIcon icon={BiWorld} className="text-2xl text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold">HEAL Ecosystem <span className="text-emerald-400 text-lg font-normal ml-2">(Health + Certification)</span></h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            {[
              { name: "Certified Fractal Therapist", val: "₹35,000" },
              { name: "Certified Acupressure Therapist", val: "₹35,000" },
              { name: "Certified Neuro Touch Healer", val: "₹35,000" },
              { name: "Certified Classical Acupuncturist", val: "₹1,00,000" },
              { name: "Certified Clinical Acupuncturist", val: "₹1,00,000" },
              { name: "Certified Health Coach", val: "₹3,00,000" },
              { name: "Doctor JV Practice Opportunity", val: "Priceless" },
              { name: "Gut Reset Program", val: "₹1,00,000" },
              { name: "Bonus Healing Courses", val: "₹5,00,000" },
            ].map((item, i) => (
              <div key={i} className="flex justify-between items-center py-3 border-b border-emerald-500/10">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-emerald-400 flex-shrink-0" />
                  <span className="text-slate-300">{item.name}</span>
                </div>
                <span className="font-mono text-emerald-400/70 text-sm">{item.val}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 text-right">
            <p className="text-2xl font-bold text-emerald-400">Total Value: ₹11L+ approx</p>
          </div>
        </div>
      </section>

      {/* CASH Ecosystem */}
      <section className="py-20 bg-gradient-to-b from-[#020617] to-amber-950/20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <SafeIcon icon={FaGem} className="text-2xl text-amber-400" />
            </div>
            <h2 className="text-3xl font-bold">CASH Ecosystem <span className="text-amber-400 text-lg font-normal ml-2">(Business + Wealth)</span></h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            {[
              { name: "24+ AI Income Courses", val: "₹3,25,768" },
              { name: "High Ticket Mastery (Mastermind)", val: "₹5,00,000" },
              { name: "Digital Product Mastery", val: "₹1,00,000" },
              { name: "Low & Mid Ticket System", val: "₹1,00,000" },
              { name: "Challenge Funnel Selling System", val: "₹1,00,000" },
              { name: "Funnel Builder Training", val: "₹3,00,000" },
              { name: "Affiliate Mastery", val: "₹10,00,000" },
              { name: "Digital Coaching Mastery", val: "₹3,00,000" },
              { name: "Train the Trainer Certification", val: "₹3,00,000" },
              { name: "Life Coaching Certification", val: "₹3,00,000" },
              { name: "Funnelogy Secrets", val: "₹3,00,000" },
            ].map((item, i) => (
              <div key={i} className="flex justify-between items-center py-3 border-b border-amber-500/10">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">{item.name}</span>
                </div>
                <span className="font-mono text-amber-400/70 text-sm">{item.val}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 text-right">
            <p className="text-2xl font-bold text-amber-400">Total Value: ₹36L+ approx</p>
          </div>
        </div>
      </section>

      {/* Total Value Stack */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-emerald-500/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10 p-8 md:p-12 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm">
          <h3 className="text-2xl md:text-3xl font-light mb-4">Total Value Inside Diamond 2.0</h3>
          <div className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-white to-amber-200 mb-6">
            ₹47,00,000+
          </div>
          <p className="text-lg text-slate-300">Worth of Systems, Certifications & Income Frameworks</p>
        </div>
      </section>

      {/* Pricing & Offer */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-xl text-slate-400 mb-2">Regular Investment</p>
          <p className="text-3xl font-bold text-slate-200 line-through decoration-red-500/50 mb-8">₹3,00,000 / year</p>
          
          <div className="p-8 rounded-2xl bg-gradient-to-b from-amber-900/40 to-[#020617] border border-amber-500/30 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-amber-400 mb-4">💎 Today’s Special Offer</h3>
            <div className="h-12 bg-white/5 rounded-lg flex items-center justify-center mb-6 relative overflow-hidden group">
              <div className="absolute inset-0 bg-white/10 backdrop-blur-md flex items-center justify-center">
                <span className="font-bold tracking-widest text-white/50">REVEALED ONLY LIVE</span>
              </div>
            </div>
            <ul className="text-left space-y-3 max-w-md mx-auto mb-8">
              {[
                "₹40,000 Instant Discount (Only LIVE)",
                "Lifetime Access Upgrade (Not Yearly)",
                "Founder-Level Bonuses",
                "Personal Roadmap to ₹1 CRORE in 2026"
              ].map((bonus, i) => (
                 <li key={i} className="flex items-center gap-3">
                   <SafeIcon icon={FiAward} className="text-amber-400 flex-shrink-0" />
                   <span className="text-slate-200">{bonus}</span>
                 </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ROI & Inaction */}
      <section className="py-20 bg-slate-900/30 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          {/* ROI */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-emerald-400">📈 The ROI Potential</h3>
            <p className="mb-6 text-slate-300">If you implement this system, you can generate:</p>
            <div className="space-y-4">
              {[
                { l: "₹1L/month", r: "₹12L/year" },
                { l: "₹3L/month", r: "₹36L/year" },
                { l: "₹10L/month", r: "₹1.2Cr/year" },
              ].map((stat, i) => (
                <div key={i} className="flex justify-between p-4 rounded-lg bg-emerald-900/10 border border-emerald-500/20">
                  <span className="font-bold text-white">{stat.l}</span>
                  <span className="text-emerald-400">{stat.r}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-400 italic">Even ONE funnel or ONE offer can recover your full investment.</p>
          </div>

          {/* Cost of Inaction */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-red-400">⚠️ Cost of Inaction</h3>
            <p className="mb-6 text-slate-300">If you delay, you risk:</p>
            <div className="space-y-4">
              {[
                "Another year of confusion",
                "Missed income opportunities",
                "No authority or certification",
                "No automated system in place",
                "Zero growth in 2024"
              ].map((risk, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-red-500">❌</span>
                  <span className="text-slate-300">{risk}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-lg font-semibold text-white">"You don’t lose money… you lose TIME"</p>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section id="registration-form" className="py-20 px-4 relative">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Secure Your Private Seat</h2>
            <div className="flex items-center justify-center gap-2 text-amber-400 font-mono text-xl">
               <span>Registration Closes In:</span>
               <span className="bg-amber-900/30 px-2 rounded">{String(timeLeft.hours).padStart(2,'0')}h</span>:
               <span className="bg-amber-900/30 px-2 rounded">{String(timeLeft.minutes).padStart(2,'0')}m</span>:
               <span className="bg-amber-900/30 px-2 rounded">{String(timeLeft.seconds).padStart(2,'0')}s</span>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 md:p-10 backdrop-blur-xl shadow-2xl">
             <div className="mb-8 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20 text-center">
                <p className="text-amber-200 text-sm">🔒 This is a Closed Door Founder Room. Only for serious applicants.</p>
             </div>
             <RegistrationForm />
          </div>

          <div className="mt-16 text-center">
            <h3 className="text-2xl md:text-3xl font-serif italic text-slate-300 mb-2">
              “This Valentine’s… Fall in Love with Your Future Income, Freedom & Legacy ❤️💎”
            </h3>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5 bg-[#01030b] text-center">
        <div className="max-w-4xl mx-auto px-4">
          <SafeIcon icon={FaGem} className="text-4xl text-amber-500 mx-auto mb-6" />
          <h4 className="text-xl font-bold text-white mb-2">Abdul Hakkim A</h4>
          <p className="text-slate-400 mb-1">Founder – FunnelsDone LLC</p>
          <p className="text-slate-400 mb-8">MD – Goguru International Pvt Ltd</p>
          <p className="text-xs text-slate-600">© 2024 Digital Millionaires Community. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;